import React from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LivesProvider } from './context/LivesContext';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AccountDeletion from './pages/AccountDeletion';
import TransactionsPage from './pages/TransactionsPage';
import GamesPage from './pages/GamesPage';
import QuestsPage from './pages/QuestsPage';
import CategoryMatcherGame from './pages/CategoryMatcherGame';
import BreakoutPage from './pages/BreakoutPage';
import HelicopterPage from './pages/HelicopterPage';
import Game2048Page from './pages/Game2048Page';
import FinancialGuruArticlePage from './pages/FinancialGuruArticlePage';
import EmergencyFundGoalPage from './pages/EmergencyFundGoalPage';
import TestQuestPage from './pages/TestQuestPage';
import DevAdminPage from './pages/DevAdminPage';
import PrivateRoute from './components/PrivateRoute';
import ProtectedGameRoute from './components/ProtectedGameRoute';
import AddExpenseButton from './components/AddExpenseButton';
import { GAME_CONFIGS } from './constants/gameConfig';
import './App.css';

const App: React.FC = () => {

  // Handle deep links for Plaid redirects on mobile
  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleAppUrlOpen = (event: any) => {
        console.log('App URL opened:', event.url);
        if (event.url && event.url.includes('wealthplay://plaid')) {
          console.log('Plaid redirect detected, processing redirect...');
          
          // Extract the redirect parameters and notify Plaid Link
          try {
            const url = new URL(event.url);
            const searchParams = new URLSearchParams(url.search);
            
            // Check if this is a successful redirect (has required Plaid parameters)
            if (searchParams.has('link_session_id') || url.hash.includes('link_session_id')) {
              console.log('Plaid redirect contains success parameters');
              
              // Trigger a custom event that Plaid Link components can listen for
              const plaidRedirectEvent = new CustomEvent('plaidRedirect', {
                detail: { url: event.url }
              });
              window.dispatchEvent(plaidRedirectEvent);
            }
          } catch (error) {
            console.error('Error processing Plaid redirect URL:', error);
          }
        }
      };

      CapApp.addListener('appUrlOpen', handleAppUrlOpen);

      return () => {
        CapApp.removeAllListeners();
      };
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <LivesProvider>
          <div className="App">
            <AddExpenseButton />
            <Routes>
              <Route path="/" element={<PrivateRoute><QuestsPage /></PrivateRoute>} />
              <Route path="/home" element={<PrivateRoute><HomePage /></PrivateRoute>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/account-deletion" element={<AccountDeletion />} />
              <Route
                path="/transactions"
                element={
                  <PrivateRoute>
                    <TransactionsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/games"
                element={
                  <PrivateRoute>
                    <GamesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/quests"
                element={
                  <PrivateRoute>
                    <QuestsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/quests/financial-guru-article"
                element={
                  <PrivateRoute>
                    <FinancialGuruArticlePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/games/category-matcher"
                element={
                  <PrivateRoute>
                    <CategoryMatcherGame />
                  </PrivateRoute>
                }
              />
              <Route
                path="/games/breakout"
                element={
                  <PrivateRoute>
                    <BreakoutPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/games/helicopter"
                element={
                  <PrivateRoute>
                    <ProtectedGameRoute requiredXP={GAME_CONFIGS.find(g => g.id === 'helicopter')?.unlockXP || 300}>
                      <HelicopterPage />
                    </ProtectedGameRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/games/2048"
                element={
                  <PrivateRoute>
                    <ProtectedGameRoute requiredXP={GAME_CONFIGS.find(g => g.id === '2048')?.unlockXP || 1000}>
                      <Game2048Page />
                    </ProtectedGameRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/quests/test-quest"
                element={
                  <PrivateRoute>
                    <TestQuestPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/quests/emergency-fund-goal"
                element={
                  <PrivateRoute>
                    <EmergencyFundGoalPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dev-admin"
                element={
                  <PrivateRoute>
                    <DevAdminPage />
                  </PrivateRoute>
                }
              />
            </Routes>
          </div>
        </LivesProvider>
      </AuthProvider>
    </Router>
  );
};

export default App; 